import React, { FC, useState } from 'react';
import { useTable, useGlobalFilter, useSortBy, usePagination } from 'react-table';
import Icon, { IconNames } from 'components/Icon';
import theme from 'config/theme';
import { IconContainer } from 'screens/Dashboard/styled';
import Spacing from 'components/Spacing';
import { useAppSelector } from 'helpers/hooks';
import { getSeriesState } from 'selectors/series';
import {
  Wrapper,
  SearchInput,
  TableContainer,
  Th,
  Tr,
  Td,
  ActionsCell,
  EmptyState,
  CardList,
  Card,
  CardField,
  CardLabel,
  CardValue,
  CardActions,
  PaginationRow,
  PaginationBtn,
  PageInfo,
} from './styled';

interface Props {
  columns?: any[];
  data: any;
  pageSize?: number;
  withPagination?: boolean;
  withDownloadCSV?: boolean;
  withColumnFilter?: boolean;
  handleDelete: (id: number) => void;
  handleEdit: (id: number) => void;
}

export const CustomTable: FC<Props> = ({
  columns,
  data,
  pageSize,
  withPagination,
  handleDelete,
  handleEdit,
}) => {
  const seriesState = useAppSelector(state => getSeriesState(state));
  const [filterValue, setFilterValue] = useState('');

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    setGlobalFilter,
    pageCount,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: pageSize || 10, pageIndex: 0 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(e.target.value);
    setGlobalFilter(e.target.value || undefined);
  };

  const resolveCellContent = (cell: any) => {
    if (cell.column.id === 'id_serie') {
      return seriesState?.data?.find((s: any) => s.id === cell.row.original.id_serie)?.title || '-';
    }
    return cell.render('Cell');
  };

  return (
    <Wrapper>
      <SearchInput
        type="text"
        placeholder="Buscar..."
        value={filterValue}
        onChange={handleSearch}
      />

      <TableContainer>
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup: any, i: number) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers.map((column: any, key: number) => (
                  <Th
                    key={key}
                    $isActions={column.id === 'actions'}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                    {column.isSorted ? (column.isSortedDesc ? ' ↓' : ' ↑') : ''}
                  </Th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <td colSpan={columns?.length || 1}>
                  <EmptyState>Sin resultados</EmptyState>
                </td>
              </tr>
            ) : (
              page.map((row: any, i: number) => {
                prepareRow(row);
                return (
                  <Tr {...row.getRowProps()} key={i} $even={i % 2 === 0}>
                    {row.cells.map((cell: any, key: number) => (
                      <Td
                        {...cell.getCellProps()}
                        key={key}
                        $isActions={cell.column.id === 'actions'}
                      >
                        {cell.column.id === 'actions' ? (
                          <ActionsCell>
                            <IconContainer
                              $type="edit"
                              onClick={() => handleEdit(cell.row.original.id)}
                            >
                              <Icon name={IconNames.Pen} color={theme.colors.white} size={16} />
                            </IconContainer>
                            <Spacing $vertical $size={8} />
                            <IconContainer
                              $type="delete"
                              onClick={() => handleDelete(cell.row.original.id)}
                            >
                              <Icon name={IconNames.Trash} color={theme.colors.white} size={16} />
                            </IconContainer>
                          </ActionsCell>
                        ) : (
                          resolveCellContent(cell)
                        )}
                      </Td>
                    ))}
                  </Tr>
                );
              })
            )}
          </tbody>
        </table>
      </TableContainer>

      <CardList>
        {page.length === 0 ? (
          <EmptyState>Sin resultados</EmptyState>
        ) : (
          page.map((row: any, i: number) => {
            prepareRow(row);
            return (
              <Card key={i}>
                {row.cells
                  .filter((cell: any) => cell.column.id !== 'actions' && cell.column.Header)
                  .map((cell: any, key: number) => (
                    <CardField key={key}>
                      <CardLabel>{cell.column.Header}</CardLabel>
                      <CardValue>{resolveCellContent(cell)}</CardValue>
                    </CardField>
                  ))}
                <CardActions>
                  <IconContainer $type="edit" onClick={() => handleEdit(row.original.id)}>
                    <Icon name={IconNames.Pen} color={theme.colors.white} size={16} />
                  </IconContainer>
                  <Spacing $vertical $size={8} />
                  <IconContainer $type="delete" onClick={() => handleDelete(row.original.id)}>
                    <Icon name={IconNames.Trash} color={theme.colors.white} size={16} />
                  </IconContainer>
                </CardActions>
              </Card>
            );
          })
        )}
      </CardList>

      {withPagination && pageCount > 1 && (
        <PaginationRow>
          <PaginationBtn onClick={previousPage} disabled={!canPreviousPage}>
            ← Anterior
          </PaginationBtn>
          <PageInfo>
            Página {pageIndex + 1} de {pageCount}
          </PageInfo>
          <PaginationBtn onClick={nextPage} disabled={!canNextPage}>
            Siguiente →
          </PaginationBtn>
        </PaginationRow>
      )}
    </Wrapper>
  );
};
